import { useEffect, useRef, useState } from "react";
import CustomSelect from "../../core/common/commonSelect";
import { OptionType } from "../../core/common/commonSelect";
import { studentsService, BasicStudent } from "../../core/services/students.service";

interface AddStudentModalProps {
    classroomId: number;
    onAddStudents: (students: OptionType[]) => void;
}

const MIN_SEARCH_LENGTH = 2;

const toOption = (student: BasicStudent): OptionType => ({
    label: `${student.name} (${student.email})`,
    value: student.id.toString(),
});

const AddStudentModal = ({ classroomId, onAddStudents }: AddStudentModalProps) => {
    const [searchResults, setSearchResults] = useState<OptionType[]>([]);
    const [searchText, setSearchText] = useState("");
    const [selectedStudents, setSelectedStudents] = useState<OptionType[]>([]);
    const [searching, setSearching] = useState(false);
    const [matchingEmails, setMatchingEmails] = useState(false);
    const [pastedEmailStatuses, setPastedEmailStatuses] = useState<{ email: string; found: boolean }[]>([]);
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
    const searchRequestId = useRef(0);

    useEffect(() => () => clearTimeout(searchTimeout.current), []);

    // Search institution students that are not in the classroom on the server, as the user types
    const handleSearch = (value: string) => {
        setSearchText(value);
        clearTimeout(searchTimeout.current);

        const term = value.trim();
        if (term.length < MIN_SEARCH_LENGTH) {
            searchRequestId.current++;
            setSearchResults([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        searchTimeout.current = setTimeout(() => {
            const requestId = ++searchRequestId.current;
            studentsService
                .getAvailable(classroomId, term)
                .then((students) => {
                    if (requestId === searchRequestId.current) {
                        setSearchResults(students.map(toOption));
                    }
                })
                .catch(() => {
                    if (requestId === searchRequestId.current) {
                        setSearchResults([]);
                    }
                })
                .finally(() => {
                    if (requestId === searchRequestId.current) {
                        setSearching(false);
                    }
                });
        }, 300);
    };

    // Keep selected students in the options so their labels stay visible
    const options = [
        ...selectedStudents,
        ...searchResults.filter((option) => !selectedStudents.some((s) => s.value === option.value)),
    ];

    const handlePasteEmails = (e: React.ClipboardEvent) => {
        // Get pasted text from clipboard
        const pastedText = e.clipboardData.getData('text');

        if (!pastedText.trim()) {
            return;
        }

        // Prevent default paste behavior to avoid pasting raw text into the select
        e.preventDefault();

        // Parse comma, semicolon, space or newline separated emails
        const uniqueEmails = Array.from(new Set(
            pastedText
                .split(/[\s,;]+/)
                .map((email) => email.trim().toLowerCase())
                .filter((email) => email.length > 0)
        ));

        if (uniqueEmails.length === 0) {
            return;
        }

        // Match the pasted emails on the server
        setMatchingEmails(true);
        studentsService
            .matchAvailable(classroomId, uniqueEmails)
            .then((students) => {
                const foundEmails = new Set(students.map((s) => s.email.toLowerCase()));
                setPastedEmailStatuses(uniqueEmails.map((email) => ({ email, found: foundEmails.has(email) })));

                // Merge with existing selected students (avoid duplicates)
                setSelectedStudents((current) => {
                    const newSelected = [...current];
                    students.map(toOption).forEach((matched) => {
                        if (!newSelected.find((s) => s.value === matched.value)) {
                            newSelected.push(matched);
                        }
                    });
                    return newSelected;
                });
            })
            .catch(() => {
                setPastedEmailStatuses(uniqueEmails.map((email) => ({ email, found: false })));
            })
            .finally(() => {
                setMatchingEmails(false);
            });
    };

    const resetSearch = () => {
        clearTimeout(searchTimeout.current);
        searchRequestId.current++;
        setSearchResults([]);
        setSearchText("");
        setSearching(false);
    };

    const handleAddStudents = () => {
        if (selectedStudents.length === 0) {
            return;
        }
        onAddStudents(selectedStudents);
        setSelectedStudents([]);
        setPastedEmailStatuses([]);
        resetSearch();
        closeModal();
    };

    const closeModal = () => {
        setSelectedStudents([]);
        setPastedEmailStatuses([]);
        const modalElement = document.getElementById("add_student_modal");
        if (modalElement) {
            const cancelButton = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLButtonElement;
            if (cancelButton) {
                cancelButton.click();
            }
        }
    };

    const handleCancel = () => {
        setSelectedStudents([]);
        setPastedEmailStatuses([]);
        resetSearch();
    };

    return (
        <div className='modal fade' id='add_student_modal'>
            <div className='modal-dialog modal-dialog-centered modal-lg'>
                <div className='modal-content'>
                    <div className='modal-body custom-modal-body'>
                        <div className='text-center mb-4'>
                            <span className='avatar avatar-lg bg-primary-transparent rounded-circle mb-2'>
                                <i className='isax isax-add-circle fs-24 text-primary' />
                            </span>
                            <h4 className='mb-2'>Add Students to Classroom</h4>
                        </div>
                        <div>
                            <div className='row'>
                                <div className='col-md-12'>
                                    <div className='mb-3'>
                                        <label className='form-label'>
                                            Students <span className='text-danger'>*</span> <small className='text-muted'>(Multiple Selection or Paste Emails)</small>
                                        </label>
                                        <CustomSelect
                                            options={options}
                                            value={selectedStudents}
                                            onChange={setSelectedStudents}
                                            placeholder={matchingEmails ? "Checking emails..." : "Type at least 2 characters or paste comma-separated emails"}
                                            multiple
                                            showSearch={true}
                                            onSearch={handleSearch}
                                            loading={searching}
                                            notFoundContent={
                                                searching
                                                    ? "Searching..."
                                                    : searchText.trim().length < MIN_SEARCH_LENGTH
                                                        ? "Type at least 2 characters to search"
                                                        : "No students found"
                                            }
                                            className='select'
                                            onPaste={handlePasteEmails}
                                        />
                                        <small className='text-muted d-block mt-2'>
                                            <i className='isax isax-info-circle me-1' />
                                            You can paste comma-separated emails directly into the field. Selected: <strong>{selectedStudents.length}</strong>
                                        </small>
                                        {pastedEmailStatuses.length > 0 && (
                                            <>
                                                <div className='mt-2 border rounded p-2' style={{ height: 240, overflowY: 'auto' }}>
                                                    {pastedEmailStatuses.map(({ email, found }) => (
                                                        <div key={email} className='d-flex align-items-center py-1'>
                                                            <i className={`isax ${found ? 'isax-tick-circle text-success' : 'isax-close-circle text-danger'} me-2`} />
                                                            <span className='text-body'>{email}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className='mt-2'>
                                                    <small className='text-muted'>
                                                        Total: <strong>{pastedEmailStatuses.length}</strong> · Matched: <strong className='text-success'>{pastedEmailStatuses.filter(s => s.found).length}</strong> · Unmatched: <strong className='text-danger'>{pastedEmailStatuses.filter(s => !s.found).length}</strong>
                                                    </small>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className='d-flex align-items-center justify-content-center gap-2 mt-4'>
                                <button className='btn bg-gray-100 rounded-pill me-2' type='button' data-bs-dismiss='modal' onClick={handleCancel}>
                                    Cancel
                                </button>
                                <button
                                    className='btn btn-primary rounded-pill'
                                    type='button'
                                    onClick={handleAddStudents}
                                    disabled={selectedStudents.length === 0 || matchingEmails}
                                >
                                    Add Students
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddStudentModal;

